import React from "react";
import { Badge, Box, chakra, Collapse, Flex, Grid, Icon, Text } from "@chakra-ui/react";
import { useReducedMotion } from "../../theme/foundations/motion";
import { motion } from "framer-motion";
import { FiChevronDown, FiFileText } from "react-icons/fi";
import { Meter } from "./Meter";

export interface ContractRowProps {
  name: string;
  /** P&L / contract type label shown as an info badge. */
  pnl: string;
  /** Pass parseDate(contract.startDate.value); null when unknown. */
  startDate: Date | null;
  endDate: Date | null;
  isOpen: boolean;
  onToggle: () => void;
  itemCount: number;
  /** id of the collapsible panel (aria-controls), e.g. `contract-${id}`. */
  panelId: string;
  /** The items table (DataTable size="sm"). */
  children: React.ReactNode;
}

const DAY = 86_400_000;
const isValid = (d: Date | null): d is Date => d instanceof Date && !Number.isNaN(d.getTime());
const pad = (n: number) => String(n).padStart(2, "0");
/** dd/mm/yyyy — the format used across the portal. */
export const formatShortDate = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

export interface ContractPeriod {
  /** 0–1 elapsed fraction */
  elapsed: number;
  daysLeft: number;
  ended: boolean;
  /** true while `now` is before the start date */
  notStarted: boolean;
}

/** Elapsed fraction and days left between two dates, or null when either date is invalid or the range is empty. */
export const getContractPeriod = (start: Date | null, end: Date | null, now = new Date()): ContractPeriod | null => {
  if (!isValid(start) || !isValid(end)) return null;
  const total = end.getTime() - start.getTime();
  if (total <= 0) return null;
  const elapsed = Math.max(0, Math.min(1, (now.getTime() - start.getTime()) / total));
  const daysLeft = Math.ceil((end.getTime() - now.getTime()) / DAY);
  return { elapsed, daysLeft, ended: daysLeft < 0, notStarted: now.getTime() < start.getTime() };
};

/**
 * One contract as a single grid <button aria-expanded> (name, P&L badge, period, item count, chevron) with a period meter,
 * followed by a Collapse panel holding the items table. `selectedRow` toggling stays in the page.
 * @example
 * <ContractRow name={c.name.value} pnl={c.pnl.text} startDate={parseDate(c.startDate.value)} endDate={parseDate(c.endDate.value)} isOpen={i === selectedRow && isOpen} onToggle={() => toggle(i)} itemCount={c.items.length} panelId={`contract-${c.id.value}`}>
 *   <DataTable size="sm">…</DataTable>
 * </ContractRow>
 */
export const ContractRow: React.FC<ContractRowProps> = ({ name, pnl, startDate, endDate, isOpen, onToggle, itemCount, panelId, children }) => {
  const reduce = useReducedMotion();
  const period = getContractPeriod(startDate, endDate);
  const dates = isValid(startDate) && isValid(endDate) ? `${formatShortDate(startDate)} → ${formatShortDate(endDate)}` : "—";

  return (
    // The divider between rows belongs to the list item wrapping each row (see contracts.tsx), so the row works standalone.
    <Box>
      <chakra.button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        w="full"
        textAlign="left"
        px={4}
        py={4}
        display="block"
        transition="background-color 120ms"
        _hover={{ bg: "sand.50" }}
        _focusVisible={{ boxShadow: "outline", borderRadius: "md", position: "relative", zIndex: 1 }}
        cursor="pointer"
      >
        {/* Everything inside the <button> renders as spans (phrasing content only); display is set explicitly. */}
        <Grid as="span" display="grid" templateColumns={{ base: "auto 1fr auto", md: "auto 2fr 1fr 1.4fr auto" }} alignItems="center" columnGap={4} rowGap={2}>
          <Box
            as="span"
            w="40px"
            h="40px"
            borderRadius="md"
            bg="navy.50"
            color="navy.700"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            aria-hidden
          >
            <Icon as={FiFileText} boxSize="18px" />
          </Box>

          <Box as="span" display="block" minW={0}>
            <Text as="span" display="block" textStyle="h4" noOfLines={1} title={name}>
              {name}
            </Text>
            {period && (
              <Flex as="span" display="flex" align="center" gap={2} mt={1} wrap="wrap">
                <Text as="span" display="block" textStyle="caption" color={period.ended ? "text.danger" : "text.muted"}>
                  {period.ended
                    ? "Ended"
                    : period.notStarted && isValid(startDate)
                      ? `Starts ${formatShortDate(startDate)}`
                      : period.daysLeft <= 0
                        ? "Ends today"
                        : `Ends in ${period.daysLeft} day${period.daysLeft === 1 ? "" : "s"}`}
                </Text>
                <Meter value={period.elapsed} max={1} tone={period.ended ? "danger" : "brand"} w="120px" label={`${Math.round(period.elapsed * 100)}% of the contract period elapsed`} />
                {/* The Meter's aria-label already announces the figure; hide the visible duplicate from the button's name. */}
                <Text as="span" display="block" textStyle="caption" color="text.muted" aria-hidden>
                  {Math.round(period.elapsed * 100)}% elapsed
                </Text>
              </Flex>
            )}
          </Box>

          <Box as="span" display={{ base: "none", md: "block" }}>
            <Badge variant="subtle-info">{pnl}</Badge>
          </Box>

          <Text as="span" textStyle="numeral" fontSize="0.875rem" color="text.secondary" display={{ base: "none", md: "block" }} whiteSpace="nowrap">
            {dates}
          </Text>

          <Flex as="span" display="flex" align="center" gap={2} flexShrink={0}>
            <Box
              as="span"
              textStyle="caption"
              bg="sand.100"
              color="text.secondary"
              borderRadius="full"
              px={3}
              h="2rem"
              display="inline-flex"
              alignItems="center"
              whiteSpace="nowrap"
            >
              Items ({itemCount})
            </Box>
            <motion.span
              aria-hidden
              style={{ display: "inline-flex" }}
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={reduce ? { duration: 0 } : { duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <Icon as={FiChevronDown} boxSize="18px" color="sand.400" />
            </motion.span>
          </Flex>

          <Flex as="span" gridColumn="2 / -1" gap={3} align="center" display={{ base: "flex", md: "none" }} wrap="wrap">
            <Badge variant="subtle-info">{pnl}</Badge>
            <Text as="span" display="block" textStyle="numeral" fontSize="0.8125rem" color="text.secondary">
              {dates}
            </Text>
          </Flex>
        </Grid>
      </chakra.button>

      <Collapse in={isOpen} animateOpacity transition={reduce ? { enter: { duration: 0 }, exit: { duration: 0 } } : undefined}>
        <Box id={panelId} bg="surface.sunken" px={{ base: 2, md: 6 }} py={4}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};
